import React, { useState } from 'react'
import {Upload, X, Send, ImagePlus} from 'lucide-react'
import { usePrints } from '../hooks/usePrints'

interface PrintUploadProps {
  onClose: () => void
  onSuccess?: () => void
}

export function PrintUpload({ onClose, onSuccess }: PrintUploadProps) {
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const { uploading, uploadMultiplePrints } = usePrints()

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    
    if (files.length === 0) return

    // Limitar a 6 imagens
    const totalImages = imageFiles.length + files.length
    if (totalImages > 6) {
      setMessage({ type: 'error', text: 'Você pode enviar no máximo 6 imagens.' })
      return
    }

    // Validar cada arquivo
    const validFiles: File[] = []
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Por favor, selecione apenas arquivos de imagem.' })
        return
      }

      if (file.size > 10 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Cada imagem deve ter no máximo 10MB.' })
        return
      }

      validFiles.push(file)
    }

    // Adicionar arquivos válidos
    setImageFiles(prev => [...prev, ...validFiles])

    // Gerar previews
    validFiles.forEach(file => {
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result as string])
      }
      reader.readAsDataURL(file)
    })

    setMessage(null)
  }

  const handleRemoveImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index))
    setImagePreviews(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (imageFiles.length === 0) {
      setMessage({ type: 'error', text: 'Por favor, selecione pelo menos uma imagem.' })
      return
    }

    const result = await uploadMultiplePrints(imageFiles)

    setMessage({
      type: result.success ? 'success' : 'error',
      text: result.message
    })

    if (result.success) {
      if (onSuccess) {
        await onSuccess()
      }
      setTimeout(() => {
        onClose()
      }, 2000)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-[#2D1B4E] to-[#1a0f2e] border-2 border-[#D4AF37]/30 rounded-3xl p-8 md:p-10 max-w-4xl w-full relative shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-purple-300 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-8">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-white mb-4">
            Enviar Prints de Depoimentos
          </h2>
          <p className="text-purple-200 text-lg mb-2">
            Faça upload de até 6 imagens para publicar na página
          </p>
          <p className="text-[#D4AF37] font-semibold">
            {imageFiles.length} / 6 imagens selecionadas
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Grid de previews */}
          {imagePreviews.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-40 object-cover rounded-xl border-2 border-[#D4AF37]/30"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition-colors shadow-lg opacity-0 group-hover:opacity-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Botão de adicionar mais imagens */}
          {imageFiles.length < 6 && (
            <label className="block cursor-pointer">
              <div className="border-2 border-dashed border-purple-500/50 rounded-xl p-8 text-center hover:border-[#D4AF37]/50 hover:bg-purple-900/20 transition-all">
                <ImagePlus className="w-12 h-12 text-[#D4AF37] mx-auto mb-3" />
                <p className="text-white text-lg mb-1">
                  {imageFiles.length === 0 ? 'Clique para selecionar imagens' : 'Adicionar mais imagens'}
                </p>
                <p className="text-purple-300 text-sm">
                  PNG, JPG, JPEG ou WEBP (máx. 10MB cada)
                </p>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                multiple
              />
            </label>
          )}

          {message && (
            <div
              className={`rounded-xl p-4 ${
                message.type === 'success'
                  ? 'bg-green-900/30 border border-green-500/50 text-green-200'
                  : 'bg-red-900/30 border border-red-500/50 text-red-200'
              }`}
            >
              {message.text}
            </div>
          )}

          <button
            type="submit"
            disabled={uploading || imageFiles.length === 0}
            className="w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#1a0f2e] px-8 py-5 rounded-full text-xl font-bold shadow-2xl hover:shadow-[#D4AF37]/50 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-3"
          >
            {uploading ? (
              <>
                <div className="w-6 h-6 border-3 border-[#1a0f2e] border-t-transparent rounded-full animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-6 h-6" />
                Enviar {imageFiles.length} Print{imageFiles.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
